import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, Bed, Mountain, Building2 } from 'lucide-react';

export default function QRCodeGenerator() {
  const baseUrl = window.location.origin;
  
  // Hodler Inn room configuration (28 rooms total)
  const roomConfig = {
    // Floor 1 - Ground Floor
    101: { beds: '1 King', location: 'Front', view: 'Runestone Mountain View' },
    102: { beds: '1 King', location: 'Front', view: 'Runestone Mountain View' },
    103: { beds: '1 King', location: 'Front', view: 'Runestone Mountain View' },
    104: { beds: '1 King', location: 'Front', view: 'Runestone Mountain View' },
    105: { beds: '2 Full', location: 'Front', view: '' },
    106: { beds: '1 King', location: 'Back', view: '' },
    107: { beds: '1 King', location: 'Back', view: '' },
    108: { beds: '1 King', location: 'Back', view: '' },
    109: { beds: '2 Full', location: 'Back', view: '' },
    110: { beds: '1 King', location: 'Back', view: '' },
    111: { beds: '2 Queen', location: 'Back', view: '' },
    112: { beds: '2 Queen', location: 'Back', view: '' },
    // Floor 2 - Upstairs
    200: { beds: '1 King', location: 'Front', view: 'Mountain View' },
    201: { beds: '2 Queen', location: 'Front', view: 'Mountain View' },
    202: { beds: '2 Queen', location: 'Front', view: 'Mountain View' },
    203: { beds: '1 Queen', location: 'Front', view: 'Mountain View' },
    204: { beds: '1 Queen', location: 'Front', view: 'Mountain View' },
    205: { beds: '1 Queen', location: 'Front', view: 'Mountain View' },
    206: { beds: '1 Queen', location: 'Front', view: 'Mountain View' },
    207: { beds: '1 Queen', location: 'Front', view: 'Mountain View' },
    208: { beds: '1 Queen', location: 'Back', view: '' },
    209: { beds: '1 Queen', location: 'Back', view: '' },
    210: { beds: '1 Queen', location: 'Back', view: '' },
    211: { beds: '1 Queen', location: 'Back', view: '' },
    212: { beds: '1 Queen', location: 'Back', view: '' },
    213: { beds: '2 Queen', location: 'Back', view: '' },
    214: { beds: '2 Queen', location: 'Back', view: '' },
    215: { beds: '2 Queen', location: 'Back', view: '' },
  };

  const floor1 = [101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112];
  const floor2 = [200, 201, 202, 203, 204, 205, 206, 207, 208, 209, 210, 211, 212, 213, 214, 215];
  
  const handlePrint = () => {
    window.print();
  };

  const RoomCard = ({ roomNum }) => {
    const config = roomConfig[roomNum];
    const hasView = config.view && config.view.length > 0;
    
    return (
      <div 
        className="border-2 border-gray-300 rounded-2xl p-5 text-center bg-white break-inside-avoid"
        style={{ pageBreakInside: 'avoid' }}
      >
        {/* Room Number */}
        <div className="bg-orange-500 text-white text-2xl font-bold py-2 px-4 rounded-lg inline-block mb-3">
          ROOM {roomNum}
        </div>
        
        {/* Room Type Badge */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
            config.beds.includes('King') ? 'bg-purple-100 text-purple-700' :
            config.beds.includes('Queen') ? 'bg-blue-100 text-blue-700' :
            'bg-green-100 text-green-700'
          }`}>
            <Bed className="w-3 h-3" />
            {config.beds}
          </span>
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
            config.location === 'Front' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
          }`}>
            <Building2 className="w-3 h-3" />
            {config.location}
          </span>
        </div>
        
        {/* Mountain View Badge */}
        {hasView && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
              <Mountain className="w-3 h-3" />
              {config.view}
            </span>
          </div>
        )}
        
        {/* QR Code */}
        <div className="bg-gray-100 p-3 rounded-xl inline-block mb-3">
          <QRCodeSVG 
            value={`${baseUrl}/#/room?room=${roomNum}`}
            size={130}
            level="H"
            includeMargin={true}
          />
        </div>
        
        {/* Info Section */}
        <div className="text-left space-y-2 text-sm">
          <p className="font-semibold text-gray-800 text-center">📱 Scan for Room Service</p>
          <div className="bg-gray-100 rounded-lg p-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-500">Wi-Fi</p>
                <p className="font-bold text-gray-800 text-sm">Hodlerinn</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Password</p>
                <p className="font-mono text-gray-800 text-sm">opendoor</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
          <span>(918) 653-7801</span>
          <span>Check-out: 11 AM</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white p-8">
      {/* Print Button - Hidden when printing */}
      <div className="print:hidden mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Hodler Inn QR Codes</h1>
        <p className="text-gray-600 mb-2">Print these QR codes and place them in each room</p>
        <p className="text-sm text-gray-500 mb-6">28 Rooms Total: 12 Ground Floor + 16 Upstairs</p>
        <button
          onClick={handlePrint}
          className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2 mx-auto hover:bg-orange-600"
        >
          <Printer className="w-5 h-5" />
          Print All QR Codes
        </button>
        
        {/* Room Type Legend */}
        <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-700">
            <Bed className="w-3 h-3" /> King Bed
          </span>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700">
            <Bed className="w-3 h-3" /> Queen Bed(s)
          </span>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700">
            <Bed className="w-3 h-3" /> Full Bed(s)
          </span>
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
            <Mountain className="w-3 h-3" /> Mountain View
          </span>
        </div>
      </div>

      {/* Lobby Kiosk QR - Full Page */}
      <div className="page-break-after mb-8 print:mb-0">
        <div className="border-4 border-orange-500 rounded-3xl p-8 max-w-xl mx-auto text-center bg-gradient-to-b from-gray-900 to-black print:border-2">
          <h1 className="text-4xl font-black text-orange-500 mb-2">WELCOME TO</h1>
          <h2 className="text-5xl font-black text-white mb-6">HODLER INN</h2>
          
          <p className="text-xl text-orange-400 font-semibold mb-6">
            No Reservation? No Problem.
          </p>
          
          <div className="bg-white p-6 rounded-2xl inline-block mb-6">
            <QRCodeSVG 
              value={`${baseUrl}/#/book?source=lobby`}
              size={200}
              level="H"
              includeMargin={true}
            />
          </div>
          
          <div className="text-left text-white space-y-3 max-w-sm mx-auto text-lg">
            <p>📱 Scan to meet <strong className="text-orange-400">Bitsy</strong></p>
            <p>✅ Complete digital check-in on your phone</p>
            <p>💰 <strong className="text-green-400">Save 10%</strong> with Crypto payment</p>
            <p>🔑 We'll bring your key immediately!</p>
          </div>
          
          <p className="text-gray-500 text-sm mt-6">
            (918) 653-7801 • 820 US-59, Heavener, OK
          </p>
        </div>
      </div>

      {/* Google Review QR - For rooms and checkout */}
      <div className="page-break-after mb-8 print:mb-0">
        <div className="border-4 border-yellow-500 rounded-3xl p-8 max-w-xl mx-auto text-center bg-gradient-to-b from-yellow-50 to-white print:border-2">
          <div className="text-6xl mb-4">⭐</div>
          <h1 className="text-3xl font-black text-gray-800 mb-2">ENJOYING YOUR STAY?</h1>
          <h2 className="text-xl font-semibold text-yellow-600 mb-6">We'd love to hear from you!</h2>
          
          <div className="bg-white border-2 border-yellow-200 p-6 rounded-2xl inline-block mb-6 shadow-lg">
            <QRCodeSVG 
              value="https://www.google.com/travel/hotels/entity/CgoI3bP937jS8Os-EAE/reviews?utm_campaign=sharing&utm_medium=link&utm_source=htls"
              size={180}
              level="H"
              includeMargin={true}
            />
          </div>
          
          <div className="text-left space-y-3 max-w-sm mx-auto text-gray-700">
            <p className="flex items-start gap-3">
              <span className="text-2xl">📱</span>
              <span>Scan with your phone camera</span>
            </p>
            <p className="flex items-start gap-3">
              <span className="text-2xl">⭐</span>
              <span>Tap <strong>"Write a Review"</strong> on Google</span>
            </p>
            <p className="flex items-start gap-3">
              <span className="text-2xl">💬</span>
              <span>Share your experience!</span>
            </p>
          </div>
          
          <p className="text-gray-500 text-sm mt-6">
            Your review helps other travelers discover our sanctuary.
          </p>
          <p className="text-yellow-600 font-semibold mt-2">
            Thank you for choosing Hodler Inn!
          </p>
        </div>
      </div>

      {/* Floor 1 - Ground Floor */}
      <div className="mb-4 print:mb-2">
        <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-orange-500 pb-2 mb-6">
          Ground Floor — Rooms 101-112
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2 print:gap-4">
          {floor1.map((roomNum) => (
            <RoomCard key={roomNum} roomNum={roomNum} />
          ))}
        </div>
      </div>

      {/* Floor 2 - Upstairs */}
      <div className="mt-8 print:mt-4 page-break-before">
        <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-orange-500 pb-2 mb-6">
          Upstairs — Rooms 200-215
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2 print:gap-4">
          {floor2.map((roomNum) => (
            <RoomCard key={roomNum} roomNum={roomNum} />
          ))}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .page-break-after {
            page-break-after: always;
          }
          .page-break-before {
            page-break-before: always;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:grid-cols-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .print\\:gap-4 {
            gap: 1rem !important;
          }
          .print\\:mb-0 {
            margin-bottom: 0 !important;
          }
          .print\\:mb-2 {
            margin-bottom: 0.5rem !important;
          }
          .print\\:mt-4 {
            margin-top: 1rem !important;
          }
          .print\\:border-2 {
            border-width: 2px !important;
          }
          h2 {
            page-break-after: avoid;
          }
        }
      `}</style>
    </div>
  );
}
